import {
    MouseEventHandler,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dropdown,
    IconButton,
    MenuItemsArray,
    Spinner,
    VerticalMenu,
} from '../../../../../components'
import { useDropdownPosition } from '../../../../../components/common/Dropdown/useDropdownPosition'
import { ReportsItem, ReportType } from '../../../../../hooks'
import { useScrollPosition } from '../../../../../hooks/useScrollPosition/useScrollPosition'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import { useExportUsageCostReport } from '@/features/Reporting/hooks/useExportUsageCostReport'
import { useExportPayablesReport } from '@/features/Reporting/hooks/useExportPayablesReport'
import useToastStore from '@/stores/useToastStore/useToastStore'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { useAppNavigation } from '@/hooks/useAppNavigation/useAppNavigation'
import { useDeleteReport } from '@/features/Reporting/hooks/useDeleteReport/useDeleteReport'
import { useRenameReport } from '@/features/Reporting/hooks/useRenameReport/useRenameReport'
import { useNavigate } from '@tanstack/react-router'
import { useDropdownClickOutside } from '@/components/common/Dropdown/useDropdownClickOutside'
import { useExportBenchmarkingReport } from '@/features/Reporting/hooks/useExportBenchmarkingReport'
import { useExportTotalEmissionsReport } from '@/features/Reporting/hooks/useExportTotalEmissionsReport'
import { useExportGHGEmissionsReport } from '@/features/Reporting/hooks/useExportGHGEmissionsReport'
import { EmailExportModal } from '@/components/organisms/EmailExportModal/EmailExportModal'
import { useExportIntervalDataReport } from '@/features/Reporting/hooks/useExportIntervalDataReport'
import { Icon } from '@/components/atoms'

type ActionsDropdownProps = {
    report: ReportsItem
    containerRef?: RefObject<HTMLDivElement>
}

export const ActionsDropdown: React.FC<ActionsDropdownProps> = (props) => {
    const { containerRef, report } = props
    const { t } = useTranslation('translations')
    const [isOpen, setIsOpen] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const { x, y } = useScrollPosition(containerRef)

    const { goToReport } = useAppNavigation()
    const { openDeleteReportModal } = useDeleteReport({
        reportId: report.customReportId,
        reportName: report.name,
    })

    const onViewClick = useCallback(() => {
        void goToReport(report.customReportId)
    }, [goToReport, report.customReportId])

    const { buttonRef, dropdownRef } = useDropdownClickOutside(() => {
        if (isOpen) {
            setIsOpen(false)
        }
    })

    const { horizontalAlign, verticalAlign } = useDropdownPosition(
        containerRef,
        dropdownRef,
        [isOpen, x, y]
    )

    const userId = useAuthStore((state) => state.user?.id) ?? ''
    const isOwner = userId === report.owner.userId

    const menuItems = useMemo<MenuItemsArray>(() => {
        const items: MenuItemsArray = [
            {
                id: 'view',
                path: '/view',
                label: t('common.view'),
                render: ({
                    renderContents,
                }: {
                    renderContents: () => React.ReactNode
                }) => (
                    <ViewMenuItem
                        onClick={onViewClick}
                        renderContents={renderContents}
                    />
                ),
            },
            {
                id: 'rename',
                path: '/rename',
                label: t('common.rename'),
                isHidden: !isOwner,
                render: ({
                    renderContents,
                }: {
                    renderContents: () => React.ReactNode
                }) => (
                    <RenameMenuItem
                        reportId={report.customReportId}
                        renderContents={renderContents}
                    />
                ),
            },
            {
                id: 'export',
                path: '/export',
                label: t('common.export'),
                render: ({
                    renderContents,
                }: {
                    renderContents: () => React.ReactNode
                }) => (
                    <ExportMenuItem
                        reportId={report.customReportId}
                        reportType={report.type}
                        renderContents={renderContents}
                        onClick={() => {
                            setIsOpen(false)
                        }}
                        onExportingChange={setIsExporting}
                    />
                ),
            },
            {
                id: 'delete',
                path: '/delete',
                label: t('common.delete'),
                isHidden: !isOwner,
                render: ({
                    renderContents,
                }: {
                    renderContents: () => React.ReactNode
                }) => (
                    <button
                        className="w-full"
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            setIsOpen(false)
                            openDeleteReportModal()
                        }}
                    >
                        {renderContents()}
                    </button>
                ),
            },
        ]

        return items
    }, [
        t,
        isOwner,
        report.type,
        report.customReportId,
        onViewClick,
        openDeleteReportModal,
        setIsExporting,
    ])

    return (
        <>
            <Dropdown
                name="report-actions-dropdown"
                dropdownRef={dropdownRef}
                buttonProps={{
                    name: 'payables-summary-actions-dropdown',
                    children: (
                        <>
                            {isExporting ? (
                                <Spinner size={16} width={3} />
                            ) : (
                                <Icon size="small" name="more_horiz" />
                            )}
                        </>
                    ),
                    variant: 'ghostSecondary',
                    size: 'large',
                    disabled: isExporting,
                    className: '[&:not(:hover):not(:focus)]:bg-[transparent]',
                    buttonRef,
                }}
                horizontalAlign={horizontalAlign}
                verticalAlign={verticalAlign}
                buttonComponent={IconButton}
                wrapperClassname="justify-end"
                dropdownClassname="py-2 min-w-tableActionsDropdownWidth"
                isOpen={isOpen}
                onClick={(event) => {
                    event.preventDefault()
                    setIsOpen((isOpen) => !isOpen)
                }}
            >
                <VerticalMenu
                    menuItems={menuItems}
                    variant={'primary'}
                    hideLabel={false}
                    menuItemClassName={'w-auto whitespace-normal break-words'}
                />
            </Dropdown>
        </>
    )
}

type ViewMenuItemProps = {
    onClick?: MouseEventHandler
    renderContents: () => React.ReactNode
}

export const ViewMenuItem: React.FC<ViewMenuItemProps> = ({
    onClick,
    renderContents,
}) => {
    return (
        <a className="cursor-pointer" onClick={onClick}>
            {renderContents()}
        </a>
    )
}

type ExportMenuItemProps = {
    reportId: string
    reportType: ReportType
    renderContents: () => React.ReactNode
    onClick?: () => void
    onExportingChange?: (isExporting: boolean) => void
}

const useExportReportFn = (reportId: string, reportType: ReportType) => {
    const { exportUsageCostReport } = useExportUsageCostReport(reportId)
    const { exportPayablesReport } = useExportPayablesReport(reportId)
    const { exportBenchmarkingReport } = useExportBenchmarkingReport(reportId)
    const { exportTotalEmissionsReport } =
        useExportTotalEmissionsReport(reportId)
    const { exportGHGEmissionsReport } = useExportGHGEmissionsReport(reportId)
    const { exportIntervalDataReport, emailExportModalProps, isExporting } =
        useExportIntervalDataReport(reportId)
    const exportFn = () => {
        if (reportType === 'usage-cost') {
            exportUsageCostReport()
        } else if (reportType === 'payables-summary') {
            exportPayablesReport()
        } else if (reportType === 'benchmarking') {
            exportBenchmarkingReport()
        } else if (reportType === 'total-emissions') {
            exportTotalEmissionsReport()
        } else if (reportType === 'interval-data') {
            exportIntervalDataReport()
        } else exportGHGEmissionsReport()
    }

    return {
        exportFn,
        emailExportModalProps:
            reportType === 'interval-data' ? emailExportModalProps : undefined,
        isExporting: reportType === 'interval-data' ? isExporting : false,
    }
}

const ExportMenuItem: React.FC<ExportMenuItemProps> = ({
    reportId,
    reportType,
    renderContents,
    onClick,
    onExportingChange,
}) => {
    const addAlert = useToastStore((state) => state.addAlert)
    const { t } = useTranslation('translations')

    const { exportFn, emailExportModalProps, isExporting } = useExportReportFn(
        reportId,
        reportType
    )

    useEffect(() => {
        onExportingChange?.(isExporting)
    }, [isExporting, onExportingChange])

    const addDownloadStartedAlert = useCallback(() => {
        addAlert({
            variant: 'success',
            title: t('reporting.export_report.success_toast.title'),
            description: t('reporting.export_report.success_toast.description'),
        })
    }, [addAlert, t])

    return (
        <>
            {emailExportModalProps && (
                <EmailExportModal {...emailExportModalProps} />
            )}
            <a
                className="cursor-pointer"
                onClick={(event) => {
                    onClick?.()
                    if (reportType !== 'interval-data') {
                        addDownloadStartedAlert()
                    }
                    event.stopPropagation()
                    exportFn()
                }}
            >
                {renderContents()}
            </a>
        </>
    )
}

type RenameMenuItemProps = {
    reportId: string
    renderContents: () => React.ReactNode
}

const RenameMenuItem: React.FC<RenameMenuItemProps> = ({
    reportId,
    renderContents,
}) => {
    const { useGetReport } = useReportsApi()
    const { refetch } = useGetReport(reportId, {
        enabled: false,
    })

    const { openRenameReportModal } = useRenameReport()
    const navigate = useNavigate()

    const handleClick = useCallback(() => {
        const handleOnSuccess = () => {
            void navigate({
                to: '/reporting',
                search: {
                    page: 1,
                    recordsPerPage: 10,
                    sortBy: 'updatedAt',
                    order: 'desc',
                },
            })
        }

        void (async () => {
            const { data: freshReport } = await refetch()

            if (freshReport) {
                openRenameReportModal(freshReport, handleOnSuccess)
            }
        })()
    }, [navigate, openRenameReportModal, refetch])

    return (
        <button className="w-full" onClick={handleClick}>
            {renderContents()}
        </button>
    )
}
